import { useState, useEffect } from 'react';
import { View } from 'react-native';

import CustomText from '../components/CustomText';
import ChipSection from '../components/ChipSection';
import MonetaImageGallery from '../components/MonetaImageGallery';
import WorkOrderLinkButton from '../components/WorkOrderLinkButton';
import { ReportHeader, ReportContent, ReportSummaryRow, ReportComments, ReportLayout, useReportData } from '../components/ReportView';
import Util from '../util/util.js';
import { Chip_Styles } from '../style/ChipStyles.js';
import { showSuccess } from '../util/error/hookErrors';
import { useResponsiveStyles, markReportViewed } from '../hooks';
import { trackEngagementAsync } from '../util/engagementTracker';
import styles from '../style/screens/reportDetailsStyles';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

const ReportDetails = (props) => {
  const { t } = useTranslation('reports');

  // Gets navigation parameters passed from previous screen
  const { uuid, noLogin, organization, location } = props.route.params;

  // Use responsive styles hook for platform-aware styling
  const {
    containerStyle,
    commentsContainerStyle,
    commentContainerStyle,
    webStyles,
    mobileStyles,
    isWeb,
  } = useResponsiveStyles();

  // Use shared report data hook - disable autoFetch for QR mode
  const {
    loading,
    report,
    content,
    timestamp,
    reporter,
    isUrgent,
    isClosed,
    imageURLs,
    comments,
    setComments,
    kpis,
    locations,
    organization: reportOrganization,
    refetch,
    editComment,
    deleteComment,
  } = useReportData(uuid, {
    defaultOrganization: organization,
    autoFetch: !noLogin, // Don't auto-fetch in QR mode
  });

  const [commentInputText, setCommentInputText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const effectiveOrganization = reportOrganization || organization || '';

  // Load current user email on mount
  useEffect(() => {
    Util.getUserEmail().then(setCurrentUserEmail);
  }, []);

  // Record that this user viewed the report (powers unread filtering).
  // QR (noLogin) sessions have no per-user identity, so skip them.
  useEffect(() => {
    if (!noLogin) {
      markReportViewed(uuid);
    }
  }, [uuid, noLogin]);

  // Handle QR sign-in before fetching
  useEffect(() => {
    const login = async () => {
      await Util.executeQRSignIn(organization, noLogin);
      refetch();
    };
    if (noLogin) {
      login();
    }
  }, []);

  // Add comment handler
  const addCommentToReport = async (commentText) => {
    const requestData = {
      queryParams: {
        uuid: uuid,
        timestamp: timestamp,
        comment: JSON.stringify(commentText),
        IsNew: true,
        reportId: Util.formatUuid(uuid),
        setFirstCommentDate: !comments || comments.length === 0,
        commentDate: Date.now(),
      },
    };

    try {
      const data = await Util.post('Resty', '/items/addComment', requestData);
      showSuccess(t('details.commentAdded'));

      // Track comment engagement for surprise badges (shows toast if badge earned)
      trackEngagementAsync('comment_reply');

      await Util.setReportCache(uuid, 'reportChanged', true);
      await Util.setReportCache(uuid, 'reportCommented', true);
      return data.items;
    } catch (error) {
      Util.printAPIError(error);
    }
    return [];
  };

  const postCommentToEntry = async () => {
    if (!commentInputText.trim() || submittingComment) {
      return;
    }

    setSubmittingComment(true);
    try {
      const newCommentData = {
        comment: commentInputText,
        commenterIsAdmin: false,
        commentDate: Date.now(),
      };

      // Optimistic update
      setComments((prev) => [...prev, newCommentData]);

      await addCommentToReport(commentInputText);
      setCommentInputText('');

      // Refetch to get server-generated comment IDs (needed for edit/delete)
      refetch();
    } catch (e) {
      console.log(e.message);
    } finally {
      setSubmittingComment(false);
    }
  };

  const headerProps = {
    organization: effectiveOrganization,
    admin: false,
    noLogin,
    location,
    type: 'pop',
  };

  const footerProps = {
    admin: false,
    organization: effectiveOrganization,
    noLogin,
    location,
  };

  // Check if we have any tags to show
  const hasKpis = kpis && kpis.length > 0;
  const hasLocations = locations && locations.length > 0;
  const hasTags = hasKpis || hasLocations;

  // At-a-glance summary data: reports predating ticketing have no status field
  const summaryStatus = report?.status || (isClosed ? 'Closed' : 'Open');
  const lastActivityAt = Math.max(
    timestamp || 0,
    ...(comments || []).map((c) => c?.commentDate || 0),
  ) || null;

  return (
    <ReportLayout
      isWeb={isWeb}
      containerStyle={containerStyle}
      headerProps={headerProps}
      footerProps={footerProps}
    >
      <ReportHeader uuid={uuid} isUrgent={isUrgent} isClosed={isClosed} webStyles={webStyles} />

      {!loading && (
        <ReportSummaryRow
          status={summaryStatus}
          kpis={kpis}
          locations={locations}
          lastUpdated={lastActivityAt}
          commentCount={comments?.length || 0}
        />
      )}

      <ReportContent content={content} timestamp={timestamp} reporter={reporter} loading={loading} />

      <MonetaImageGallery images={imageURLs} />

      {/* Tags Card - V3 card styling */}
      {hasTags && (
        <View style={styles.sectionCard}>
          <CustomText style={styles.cardTitle} font="semiBold" size={18} color={AppStyles.colorSet.textPrimary}>
            {t('details.tags')}
          </CustomText>

          {/* Hazard Type */}
          {hasKpis && (
            <>
              <CustomText style={styles.subsectionHeadingFirst} font="semiBold" size={16} color={AppStyles.colorSet.textSecondary}>
                {t('details.hazardType')}
              </CustomText>
              <ChipSection styles={Chip_Styles} Chips={kpis.sort()} doSynchronize={false} />
            </>
          )}

          {/* Location */}
          {hasLocations && (
            <>
              <CustomText style={hasKpis ? styles.subsectionHeading : styles.subsectionHeadingFirst} font="semiBold" size={16} color={AppStyles.colorSet.textSecondary}>
                {t('details.location')}
              </CustomText>
              <ChipSection styles={Chip_Styles} Chips={locations.sort()} doSynchronize={false} />
            </>
          )}
        </View>
      )}

      {/* Work Order link - only shown when the org has it configured */}
      <WorkOrderLinkButton organization={effectiveOrganization} />

      {/* Follow Up Card - V3 card styling */}
      <View style={styles.sectionCard}>
        <CustomText style={styles.cardTitle} font="semiBold" size={18} color={AppStyles.colorSet.textPrimary}>
          {t('details.followUp')}
        </CustomText>

        <ReportComments
          comments={comments}
          isAdmin={false}
          isClosed={isClosed}
          showInput={true}
          alwaysShowInput={true}
          inputValue={commentInputText}
          onInputChange={setCommentInputText}
          onSubmit={postCommentToEntry}
          submitting={submittingComment}
          webStyles={webStyles}
          mobileStyles={mobileStyles}
          commentsContainerStyle={commentsContainerStyle}
          commentContainerStyle={commentContainerStyle}
          isWeb={isWeb}
          currentUserEmail={currentUserEmail}
          isAnonymousSession={!!noLogin}
          onEditComment={editComment}
          onDeleteComment={deleteComment}
        />
      </View>

      {/* Report ID Footer - subtle, not in a card */}
      <View style={styles.reportIdContainer}>
        <CustomText style={styles.reportIdLabel} font="medium" size={14} color={AppStyles.colorSet.textMuted}>
          {t('details.reportId')}
        </CustomText>
        <CustomText font="regular" size={12} color={AppStyles.colorSet.textMuted}>
          {uuid}
        </CustomText>
      </View>
    </ReportLayout>
  );
};

export default ReportDetails;
